import { VariantProps } from 'class-variance-authority';
import { useSelector } from 'react-redux';

import { printZPL } from '@legacy-services/printer';

import { redeemAccessVoucher } from '@shared/access-vouchers/endpoints/redeemAccessVoucher';
import { reredeemAccessVoucher } from '@shared/access-vouchers/endpoints/reredeemAccessVoucher';
import { Button } from '@shared/ui/components/Button';
import { buttonVariants } from '@shared/ui/components/Button/Button.cva';
import { Icon } from '@shared/ui/components/Icon';
import { useToast } from '@shared/ui/components/Toast';
import { confetti } from '@shared/utils/helpers';

import { getPrinterForDayPeriod } from '@admin/printers/helpers';

import { printerSettings as selectPrinterSettings } from '../../../../redux/printers/selectors';

type AccessVoucher = App.Domain.Wristband.Data.AccessVoucherData;

type Props = Omit<VariantProps<typeof buttonVariants>, 'interactive'> & {
  accessVoucher: AccessVoucher;
  reprint?: boolean;
  onPrint?: () => void;
};

export default function PrintWristbandButton({ accessVoucher, variant, reprint = false, onPrint }: Props) {
  const { toast } = useToast();
  const printerSettings = useSelector(selectPrinterSettings);

  async function handlePrint() {
    try {
      const day = accessVoucher.accessVoucherTemplate.day || 'combi';
      const period = accessVoucher.accessVoucherTemplate.period;

      const printer = await getPrinterForDayPeriod(day, period, printerSettings);

      const voucherAction = reprint ? reredeemAccessVoucher : redeemAccessVoucher;
      const wristband = await voucherAction(accessVoucher.crewMember!.registrationId, accessVoucher.id);

      const zpl = atob(wristband.zpl);

      await printZPL(printer, zpl);

      confetti(true);
      toast({ title: 'Wristband Printed', description: 'The wristband was sent to the printer.' });

      onPrint?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || error?.toString() || 'unknown';
      toast({ title: 'Error', description: `Error printing wristband: ${errorMessage}`, variant: 'destructive' });
    }
  }

  if (reprint && !accessVoucher.redeemedAt) {
    return null;
  }

  if (!reprint && accessVoucher.redeemedAt) {
    return null;
  }

  return (
    <Button size="sm" variant={variant} onClick={handlePrint}>
      <Icon icon="print" />
      {reprint ? 'Reprint' : 'Print'}
    </Button>
  );
}
